import { useEffect } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import { useHasGroup } from "@/hooks/useHasGroup";
import { Button } from "@/components/ui/base/button";
import { CreateMerchantBloc } from "@/application/tools/create_merchant_bloc";
import { createMerchantRepository } from "@/application/tools/create_merchant_repository";
import { PresetPicker } from "./sections/PresetPicker";
import { IdentitySection } from "./sections/IdentitySection";
import { BusinessSection } from "./sections/BusinessSection";
import { StripeSection } from "./sections/StripeSection";
import { EntitiesSection } from "./sections/EntitiesSection";
import { JobProgressPanel } from "./JobProgressPanel";
import { DevelopersOnlyCard } from "../components/DevelopersOnlyCard";

function ProdDisabledBanner() {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        Disabled in production. The dev merchant factory only runs against
        staging/sandbox environments.
      </p>
    </div>
  );
}

interface StickyFooterProps {
  count: number;
  onCountChange: (n: number) => void;
  submitting: boolean;
  error: string | null;
  disabled: boolean;
  onSubmit: () => void;
}

function StickyFooter({
  count,
  onCountChange,
  submitting,
  error,
  disabled,
  onSubmit,
}: StickyFooterProps) {
  return (
    <div className="border-border bg-card/95 sticky bottom-0 -mx-4 mt-6 border-t p-4 backdrop-blur">
      {error && (
        <p className="mb-2 text-sm font-medium text-red-600">{error}</p>
      )}
      <div className="flex items-center gap-3">
        <label className="text-muted-foreground flex items-center gap-2 text-sm">
          Count
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) =>
              onCountChange(Math.max(1, Number(e.target.value) || 1))
            }
            disabled={disabled || submitting}
            className="border-border w-16 rounded-md border px-2 py-1 text-sm"
          />
        </label>
        <Button
          onClick={onSubmit}
          disabled={disabled || submitting}
          className="flex-1"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting…
            </>
          ) : (
            `Create ${count > 1 ? `${count} merchants` : "merchant"}`
          )}
        </Button>
      </div>
    </div>
  );
}

export function CreateMerchantView() {
  const [state, bloc] = useStateMachine(
    () => new CreateMerchantBloc(createMerchantRepository),
  );
  const isDeveloper = useHasGroup("CPOS-Developers");
  const isProd = import.meta.env.VITE_HUB_ENVIRONMENT === "production";

  useEffect(() => {
    void bloc.resumeIfRunning();
  }, [bloc]);

  if (!isDeveloper) return <DevelopersOnlyCard />;

  const { phase, draft, count } = state;
  const tracking = phase.kind === "tracking" ? phase.job : null;

  return (
    <div className="mx-auto max-w-3xl pb-24">
      <h1 className="text-foreground mb-2 text-2xl font-bold">
        Create Merchant
      </h1>
      <p className="text-muted-foreground mb-4 text-sm">
        Manufacture a fully functional test merchant — Cognito login, business
        profile, Stripe test-mode account, transactions, lifecycle.
      </p>
      {isProd && <ProdDisabledBanner />}
      {tracking ? (
        <JobProgressPanel
          job={tracking}
          createConnectedAccount={draft.stripe.createConnectedAccount}
          onReset={() => bloc.reset()}
        />
      ) : (
        <>
          <PresetPicker bloc={bloc} disabled={isProd} />
          <IdentitySection draft={draft} bloc={bloc} disabled={isProd} />
          <BusinessSection draft={draft} bloc={bloc} disabled={isProd} />
          <StripeSection draft={draft} bloc={bloc} disabled={isProd} />
          <EntitiesSection draft={draft} bloc={bloc} disabled={isProd} />
          <StickyFooter
            count={count}
            onCountChange={(n) => bloc.setCount(n)}
            submitting={phase.kind === "submitting"}
            error={phase.kind === "error" ? phase.message : null}
            disabled={isProd}
            onSubmit={() => void bloc.submit()}
          />
        </>
      )}
    </div>
  );
}
